import type { Namespace } from '../types';

// Procurement (R-234) — the site-local buying chain, rewritten to the team's
// HTML prototype: request → approve → order → receive. Five screens share this
// namespace because they are one workflow, and an operator moving between them
// should not meet a different vocabulary for the same noun.
//
// ⚠ There is no ICU pluralisation in this layer (pitfalls #6) — every count
// string is written so the singular/plural distinction never has to be made.
export const procurement = {
  // ─── shared ────────────────────────────────────────────────────────────────
  navSection: { de: 'Beschaffung', en: 'Procurement' },

  stateDraft: { de: 'Entwurf', en: 'Draft' },
  statePendingApproval: { de: 'Genehmigung ausstehend', en: 'Pending approval' },
  stateApproved: { de: 'Genehmigt', en: 'Approved' },
  stateRejected: { de: 'Abgelehnt', en: 'Rejected' },
  statePoIssued: { de: 'Bestellt', en: 'Ordered' },
  statePartiallyReceived: { de: 'Teilweise erhalten', en: 'Partially received' },
  stateReceived: { de: 'Erhalten', en: 'Received' },
  stateClosed: { de: 'Abgeschlossen', en: 'Closed' },
  stateCancelled: { de: 'Storniert', en: 'Cancelled' },

  apprWaiting: { de: 'Wartet', en: 'Waiting' },
  apprPending: { de: 'Offen', en: 'Pending' },
  apprApproved: { de: 'Genehmigt', en: 'Approved' },
  apprRejected: { de: 'Abgelehnt', en: 'Rejected' },
  apprSkippedSelf: { de: 'Übersprungen — eigener Antrag', en: 'Skipped — own request' },
  apprNotRequired: { de: 'Nicht erforderlich', en: 'Not required' },

  poOpen: { de: 'Offen', en: 'Open' },
  poCancelled: { de: 'Storniert', en: 'Cancelled' },

  domainOffice: { de: 'Büro', en: 'Office' },
  domainFarm: { de: 'Farm', en: 'Farm' },
  domainAny: { de: 'Alle Domänen', en: 'Any domain' },

  modeAll: { de: 'Alle', en: 'All' },
  modeAny: { de: 'Eine/r', en: 'Any' },

  filterAllStates: { de: 'Alle Status', en: 'All states' },
  search: { de: 'Suchen…', en: 'Search…' },
  mineOnly: { de: 'Nur meine', en: 'Mine only' },

  // ─── readiness (the empty state that blocks the whole module) ──────────────
  notReadyTitle: { de: 'Beschaffung ist noch nicht einsatzbereit', en: 'Procurement is not ready yet' },
  notReadyBody: {
    de: 'Diese Dinge müssen existieren, bevor ein Antrag gestellt werden kann:',
    en: 'These have to exist before a request can be raised:',
  },
  missingLocation: { de: 'Ein Standort', en: 'A location' },
  missingAssetClass: { de: 'Eine Anlagenklasse', en: 'An asset class' },
  missingModel: { de: 'Ein Modell im Katalog', en: 'A model in the catalogue' },
  missingApprovalRule: { de: 'Eine Genehmigungsregel', en: 'An approval rule' },
  missingRuleWhy: {
    de: 'Ohne Regel wird jeder Antrag ohne Prüfung automatisch genehmigt.',
    en: 'With no rule, every request is auto-approved with nobody reviewing it.',
  },
  // The readiness checklist over the requests list: every prerequisite, present or not.
  readinessProgress: { de: '{done} von {total} vorhanden', en: '{done} of {total} in place' },
  readinessInPlace: { de: 'Vorhanden', en: 'In place' },
  readinessMissing: { de: 'Fehlt', en: 'Missing' },
  readinessSetUp: { de: 'Einrichten', en: 'Set up' },
  readinessLoadError: {
    de: 'Es konnte nicht geprüft werden, ob die Beschaffung eingerichtet ist. Neue Anträge sind bis dahin nicht möglich.',
    en: "Couldn't check whether procurement is set up. New requests are unavailable until it loads.",
  },

  // ─── requests list ────────────────────────────────────────────────────────
  requestsTitle: { de: 'Anträge', en: 'Requests' },
  requestsDesc: {
    de: 'Bedarfsanträge und ihre Positionen — Entwurf bis Abschluss.',
    en: 'Purchase requests and their line items — draft through close.',
  },
  navRequests: { de: 'Anträge', en: 'Requests' },
  newRequest: { de: '+ Neuer Antrag', en: '+ New Request' },
  noRequests: { de: 'Noch keine Anträge.', en: 'No requests yet.' },
  noRequestsMatch: { de: 'Kein Antrag passt zum Filter.', en: 'No request matches the filter.' },

  colNumber: { de: 'Nummer', en: 'Number' },
  colRequestedFor: { de: 'Für', en: 'For' },
  colLocation: { de: 'Standort', en: 'Location' },
  colDomain: { de: 'Domäne', en: 'Domain' },
  colValue: { de: 'Wert', en: 'Value' },
  colLines: { de: 'Positionen', en: 'Line items' },
  colState: { de: 'Status', en: 'State' },
  colCreated: { de: 'Erstellt', en: 'Created' },
  colNeedBy: { de: 'Benötigt bis', en: 'Need by' },

  // ─── request detail ───────────────────────────────────────────────────────
  requestTitle: { de: 'Antrag', en: 'Request' },
  backToRequests: { de: '← Anträge', en: '← Requests' },
  fieldRequestedFor: { de: 'Angefordert für', en: 'Requested for' },
  fieldLocation: { de: 'Standort', en: 'Location' },
  fieldDomain: { de: 'Domäne', en: 'Domain' },
  fieldNeedBy: { de: 'Benötigt bis', en: 'Need by' },
  fieldJustification: { de: 'Begründung', en: 'Justification' },
  justificationPlaceholder: {
    de: 'Warum wird das gebraucht?',
    en: 'Why is this needed?',
  },
  selectLocation: { de: '— Standort wählen —', en: '— select a location —' },
  raisedBy: { de: 'Erstellt von', en: 'Raised by' },
  totalValue: { de: 'Gesamtwert', en: 'Total value' },

  submittedAt: { de: 'Eingereicht', en: 'Submitted' },
  decidedAt: { de: 'Entschieden', en: 'Decided' },
  linesTitle: { de: 'Positionen', en: 'Line items' },
  noLines: {
    de: 'Noch keine Positionen. Ein Antrag ohne Position kann nicht eingereicht werden.',
    en: 'No lines yet. A request with no lines cannot be submitted.',
  },
  addLine: { de: '+ Position', en: '+ Add line' },
  colClass: { de: 'Klasse', en: 'Class' },
  colModel: { de: 'Modell', en: 'Model' },
  colQty: { de: 'Menge', en: 'Qty' },
  colUnitEstimate: { de: 'Schätzpreis/Stk.', en: 'Est. unit cost' },
  colOrdered: { de: 'Bestellt', en: 'Ordered' },
  colReceivedQty: { de: 'Erhalten', en: 'Received' },
  colNotes: { de: 'Notiz', en: 'Notes' },
  selectClass: { de: '— Klasse —', en: '— class —' },
  selectModel: { de: '— Modell —', en: '— model —' },
  noEligibleModels: {
    de: 'Kein bestellbares Modell für diese Klasse und Domäne.',
    en: 'No orderable model for this class and domain.',
  },
  pickDomainFirst: {
    de: 'Zuerst die Domäne wählen — sie entscheidet, was bestellt werden darf.',
    en: 'Pick the domain first — it decides what may be ordered.',
  },
  qtyInvalid: {
    de: 'Menge muss eine ganze Zahl über 0 sein.',
    en: 'Quantity has to be a whole number above zero.',
  },
  saveLine: { de: 'Übernehmen', en: 'Save line' },
  removeLine: { de: 'Entfernen', en: 'Remove' },

  // ─── the progress stepper ─────────────────────────────────────────────────
  // Shorter than the state words on purpose: a stepper reads as a journey
  // ("Approval"), a badge reads as a status ("Pending approval").
  stepDraft: { de: 'Entwurf', en: 'Draft' },
  stepApproval: { de: 'Genehmigung', en: 'Approval' },
  stepApproved: { de: 'Genehmigt', en: 'Approved' },
  stepOrdered: { de: 'Bestellt', en: 'Ordered' },
  stepReceived: { de: 'Erhalten', en: 'Received' },
  stepClosed: { de: 'Abgeschlossen', en: 'Closed' },
  endedRejected: {
    de: 'Abgelehnt — die Kette endet hier. Kopieren Sie den Antrag in einen neuen Entwurf, um es erneut zu versuchen.',
    en: 'Rejected — the chain ends here. Copy it to a new draft to try again.',
  },
  endedCancelled: {
    de: 'Storniert — die Kette endet hier.',
    en: 'Cancelled — the chain ends here.',
  },

  // ─── the record header (two columns, same rows draft or locked) ───────────
  sectionRequest: { de: 'Antrag', en: 'Request' },
  // R-304: the second header card — the case for the request and what it costs,
  // beside the provenance facts that answer "who raised this, and when".
  sectionCase: { de: 'Begründung & Wert', en: 'Justification & value' },
  fieldSite: { de: 'Standort', en: 'Site' },
  selectSite: { de: '— Standort wählen —', en: '— select a site —' },
  noSites: { de: '— kein Standort vorhanden —', en: '— no sites —' },
  requestedForHint: {
    de: 'Standardmäßig Sie selbst. Ändern Sie es, wenn Sie für jemand anderen beantragen.',
    en: 'Defaults to you. Change it when raising on someone else’s behalf.',
  },
  /** Marks the caller's own row in the "requested for" picker. */
  optionYou: {
    de: '(Sie)',
    en: '(you)',
  },
  siteHint: {
    de: 'Der Typ des Standorts setzt die Domäne unten vor.',
    en: 'The site’s type defaults the domain below.',
  },
  domainHint: {
    de: 'Vom Standort vorgegeben, überschreibbar. Bestimmt, welche Klassen und Modelle beantragt werden dürfen — Berechtigung, nicht physischer Ort.',
    en: 'Defaulted from the site, overridable. Filters which classes and models you may request — eligibility, not physical location.',
  },
  justificationHint: {
    de: 'Genehmiger lesen das zuerst.',
    en: 'Approvers read this before anything else.',
  },
  raised: { de: 'Erstellt', en: 'Raised' },
  estimatedValue: { de: 'Geschätzter Wert', en: 'Estimated value' },
  // Record page, summary-rail layout (2026-09-30).
  raisedByFor: { de: 'Erstellt von {by} für {for}', en: 'Raised by {by} for {for}' },
  raisedBySelf: { de: 'Erstellt von {by}', en: 'Raised by {by}' },
  sectionDetails: { de: 'Details', en: 'Details' },
  stepLevel: { de: '· Stufe {level} von {total}', en: '· level {level} of {total}' },
  linesLocked: { de: 'Seit der Einreichung gesperrt', en: 'Locked since submission' },
  pickSiteFirst: {
    de: 'Zuerst den Standort wählen — Klassen und Modelle folgen seiner Domäne.',
    en: 'Pick the site first — the class and model lists follow its domain.',
  },
  chainNeedsDomain: {
    de: 'Die Genehmigungskette erscheint, sobald Standort und Domäne gesetzt sind.',
    en: 'The approval chain appears once the site and domain are set.',
  },
  colItem: { de: 'Artikel', en: 'Item' },
  colFulfilment: { de: 'Erfüllung', en: 'Fulfilment' },
  fulfilmentCounts: {
    de: '{ordered} bestellt · {received} erhalten',
    en: '{ordered} ordered · {received} received',
  },
  estimatedTotal: { de: 'Geschätzte Summe', en: 'Estimated total' },
  dueToday: { de: 'heute', en: 'today' },
  dueTomorrow: { de: 'morgen', en: 'tomorrow' },
  dueInDays: { de: 'in {n} Tagen', en: 'in {n} days' },
  overdueDays: { de: 'seit {n} Tagen überfällig', en: '{n} days overdue' },
  overdueDay: { de: 'seit 1 Tag überfällig', en: '1 day overdue' },
  estimatedValueHint: {
    de: 'Aus den Katalogpreisen. Der reale Preis wird auf der Bestellung erfasst.',
    en: 'From catalogue unit costs. The real price is captured on the purchase order.',
  },

  // ─── the "what do I do next" banners on a draft ───────────────────────────
  nextStep1: {
    de: 'Schritt 1 von 2 — beschreiben Sie den Antrag.',
    en: 'Step 1 of 2 — describe the request.',
  },
  nextStep1Body: {
    de: 'Wählen Sie den Standort, der die Domäne vorgibt, und sagen Sie, warum es gebraucht wird. Eine erste Position wartet unten.',
    en: 'Pick the site, which defaults the domain, and say why it is needed. A first line item is waiting below.',
  },
  nextStep2: { de: 'Nächster Schritt — Positionen erfassen.', en: 'Next step — add your line items.' },
  nextStep2Later: { de: 'Danach — Positionen erfassen.', en: 'Then — add your line items.' },
  nextStep2Seeded: {
    de: 'Die Zeile unten steht bereits auf dem neuesten Modell im Katalog — ändern Sie Klasse, Modell oder Menge, falls das nicht passt, und dann Hinzufügen.',
    en: 'The row below is already started on the newest model in the catalogue — change the class, model or quantity if it is not what you want, then Add.',
  },
  nextStep2NeedSite: {
    de: 'Die Klassen- und Modell-Listen werden nach Domäne gefiltert, der Standort oben muss also zuerst gewählt sein.',
    en: 'The class and model lists are filtered by the domain, so the site above has to be picked first.',
  },
  nextStep2Pick: {
    de: 'Wählen Sie die Anlagenklasse, dann das Modell.',
    en: 'Pick the asset class, then the model.',
  },
  needsOneLine: {
    de: 'Ein Antrag braucht mindestens eine Position, bevor er eingereicht werden kann.',
    en: 'A request needs at least one line before it can be submitted.',
  },

  // ─── the inline line-item editor ──────────────────────────────────────────
  colLine: { de: 'Pos.', en: 'Line' },
  colLineTotal: { de: 'Schätzsumme', en: 'Est. line total' },
  lineNew: { de: 'neu', en: 'new' },
  lineAdd: { de: 'Hinzufügen', en: 'Add' },
  lineDiscard: { de: 'Verwerfen', en: 'Discard' },
  lineDone: { de: 'Fertig', en: 'Done' },
  notesPlaceholder: { de: 'Für den Einkauf', en: 'For the buyer' },
  eligibleInDomain: {
    de: '{shown} von {total} in Domäne {domain} zulässig.',
    en: '{shown} of {total} eligible in {domain}.',
  },
  orderableModels: { de: '{n} bestellbare(s) Modell(e).', en: '{n} orderable model(s).' },
  costFromCatalogue: {
    de: 'Aus dem Katalog. Der reale Preis steht auf der Bestellung.',
    en: 'From the catalogue. Real price goes on the purchase order.',
  },
  catalogueFilterNote: {
    de: 'Die Klassenliste ist auf die Domäne {domain} gefiltert, die Modelle auf die Klasse und diese Domäne.',
    en: 'Class list is filtered to the {domain} domain, and models to the class and that domain.',
  },
  noOrderableUnderClass: {
    de: 'Kein bestellbares Modell unter dieser Klasse für die Domäne {domain}.',
    en: 'No orderable model under this class for the {domain} domain.',
  },
  // R-239: the way OUT of that dead end (the prototype's own button, V8:6242).
  createModel: { de: 'Modell anlegen', en: 'Create one' },
  // R-239 second pass: an empty cost box under "From the catalogue" reads as
  // broken. Name the real state, and the way to fix it.
  costNoCataloguePrice: {
    de: 'Dieses Modell hat keinen Katalogpreis. Preis hier eintragen oder von der Verwaltung hinterlegen lassen.',
    en: 'No catalogue price for this model. Type one here, or have a steward set it.',
  },
  needByHint: {
    de: 'Frühestens heute — ein Bedarfsdatum in der Vergangenheit kann nicht erfüllt werden.',
    en: 'Today at the earliest — a need-by in the past cannot be met.',
  },

  // ─── the approval chain, grouped by level ─────────────────────────────────
  modeAnyLong: { de: 'eine/r genügt', en: 'any one may approve' },
  modeAllLong: { de: 'alle müssen genehmigen', en: 'all must approve' },
  levelSatisfied: { de: 'erfüllt', en: 'satisfied' },
  levelRejected: { de: 'abgelehnt', en: 'rejected' },
  levelAwaiting: { de: 'wartet', en: 'awaiting' },
  levelNotReached: { de: 'nicht erreicht', en: 'not reached' },
  yourApprovalRequired: { de: 'Ihre Genehmigung ist erforderlich', en: 'Your approval is required' },
  yourApprovalBody: {
    de: 'auf Stufe {level} ({rule}). Mit der Genehmigung geht der Antrag zur nächsten Stufe, falls es eine gibt.',
    en: 'at level {level} ({rule}). Approving passes it to the next level, if there is one.',
  },
  skippedSelfNote: {
    de: 'Antragsteller können ihren eigenen Antrag nicht genehmigen — übersprungen.',
    en: 'Requester cannot approve their own request — skipped.',
  },

  // ⚠ The hole this names: separation of duties becoming NO duties.
  unreviewedTitle: { de: 'Von niemandem geprüft', en: 'Nobody reviewed this' },
  unreviewedBody: {
    de: 'Dieser Antrag wurde genehmigt, ohne dass eine Person zugestimmt hat — entweder griff keine Regel, oder jede greifende Regel nannte die antragstellende Person selbst und wurde übersprungen. Prüfen Sie die Genehmigungsregeln, bevor Sie bestellen.',
    en: 'This request was approved without anyone signing it — either no rule matched, or every rule that did named the requester and was skipped. Check the approval rules before ordering against it.',
  },
  rulesAllOnePerson: {
    de: 'Alle aktiven Regeln nennen {name}. Jeder Antrag, den diese Person stellt, wird ohne Prüfung genehmigt — niemand darf den eigenen Antrag genehmigen, und es gibt sonst niemanden. Fügen Sie eine zweite genehmigende Person hinzu.',
    en: 'Every active rule names {name}. Any request they raise is approved with nobody reviewing it — a requester may never approve their own request, and there is no one else. Add a second approver.',
  },

  chainTitle: { de: 'Genehmigungskette', en: 'Approval chain' },
  chainPreviewTitle: { de: 'Vorschau der Genehmigungskette', en: 'Approval chain preview' },
  chainLevel: { de: 'Stufe {level}', en: 'Level {level}' },
  chainEmpty: { de: 'Keine Regel greift.', en: 'No rule matches.' },
  // Follows `chainEmpty` ("No rule matches.") in one note, so it says only the consequence.
  chainAutoApproveWarning: {
    de: 'Dieser Antrag wird ohne Prüfung genehmigt.',
    en: 'This request will be approved with nobody reviewing it.',
  },
  chainClassRulesExcluded: {
    de: 'Klassenbezogene Regeln sind nicht bewertet, solange keine Position erfasst ist.',
    en: 'Class-scoped rules are not evaluated until a line exists.',
  },
  chainComment: { de: 'Kommentar', en: 'Comment' },

  actionSubmit: { de: 'Einreichen', en: 'Submit' },
  actionCancel: { de: 'Stornieren', en: 'Cancel request' },
  actionClose: { de: 'Antrag abschließen', en: 'Close request' },
  actionClone: { de: 'In neuen Entwurf kopieren', en: 'Copy to a new draft' },
  actionIssuePo: { de: 'Bestellung auslösen', en: 'Issue purchase order' },
  actionDiscard: { de: 'Entwurf verwerfen', en: 'Discard draft' },

  confirmSubmitTitle: { de: 'Antrag einreichen?', en: 'Submit this request?' },
  confirmCancelTitle: { de: 'Antrag stornieren?', en: 'Cancel this request?' },
  cancelReason: { de: 'Grund', en: 'Reason' },
  cancelReasonPh: {
    de: 'Warum wird diese Bestellung storniert?',
    en: 'Why is this order being cancelled?',
  },
  colActions: { de: 'Aktionen', en: 'Actions' },
  cancelReasonRequired: { de: 'Ein Grund ist erforderlich.', en: 'A reason is required.' },
  submittedAutoApproved: {
    de: 'Eingereicht — und sofort genehmigt, weil keine Regel greift.',
    en: 'Submitted — and approved immediately, because no rule matched.',
  },
  submittedOk: { de: 'Eingereicht zur Genehmigung.', en: 'Submitted for approval.' },
  cancelledOk: { de: 'Antrag storniert.', en: 'Request cancelled.' },
  closedOk: { de: 'Antrag abgeschlossen.', en: 'Request closed.' },
  clonedOk: { de: 'In einen neuen Entwurf kopiert.', en: 'Copied to a new draft.' },
  cancelReasonShown: { de: 'Stornogrund', en: 'Cancellation reason' },
  keepRequest: { de: 'Behalten', en: 'Keep request' },
  confirmCloseTitle: { de: 'Antrag abschließen?', en: 'Close this request?' },
  confirmCloseBody: {
    de: 'Ein abgeschlossener Antrag ist beendet: Es kann nichts mehr bestellt oder eingebucht werden. Offene Mengen bleiben offen.',
    en: 'A closed request is finished: nothing more can be ordered or received against it. Any outstanding quantity stays unfilled.',
  },
  confirmRemoveLineTitle: { de: 'Position {line} entfernen?', en: 'Remove line {line}?' },
  confirmRemoveLineBody: {
    de: '{model} × {qty} wird aus dem Entwurf entfernt.',
    en: '{model} × {qty} will be taken off this draft.',
  },
  lineAddedOk: { de: 'Position hinzugefügt.', en: 'Line added.' },
  lineRemovedOk: { de: 'Position entfernt.', en: 'Line removed.' },
  requestNotFound: { de: 'Antrag nicht gefunden', en: 'Request not found' },
  requestNotFoundBody: {
    de: 'Er wurde gelöscht, oder der Link ist unvollständig.',
    en: 'It may have been discarded, or the link is incomplete.',
  },
  requestLoadError: { de: 'Antrag konnte nicht geladen werden', en: "Couldn't load this request" },
  reviewInApprovals: { de: 'In „Meine Genehmigungen“ prüfen', en: 'Review in My Approvals' },
  qtyOverOutstanding: {
    de: 'Höchstens {max} für {model} — mehr ist nicht offen.',
    en: 'At most {max} for {model} — that is all that is outstanding.',
  },
  priceInvalid: {
    de: 'Der Stückpreis für {model} ist keine gültige Zahl.',
    en: 'The unit price for {model} is not a valid amount.',
  },

  // ─── my approvals ─────────────────────────────────────────────────────────
  approvalsTitle: { de: 'Meine Genehmigungen', en: 'My Approvals' },
  approvalsDesc: {
    de: 'Entscheidungen, die auf Sie warten.',
    en: 'The decisions waiting on you.',
  },
  navApprovals: { de: 'Meine Genehmigungen', en: 'My Approvals' },
  noApprovals: { de: 'Nichts wartet auf Sie.', en: 'Nothing is waiting on you.' },
  colRecord: { de: 'Vorgang', en: 'Record' },
  colRule: { de: 'Regel', en: 'Rule' },
  colLevel: { de: 'Stufe', en: 'Level' },
  colRequester: { de: 'Antragsteller', en: 'Requester' },
  approve: { de: 'Genehmigen', en: 'Approve' },
  reject: { de: 'Ablehnen', en: 'Reject' },
  decideCommentLabel: { de: 'Kommentar (optional)', en: 'Comment (optional)' },
  phDecideComment: { de: 'Optionale Notiz für die anfragende Person', en: 'Optional note for the requester' },
  phDecideReason: {
    de: 'Warum wird abgelehnt? Die anfragende Person sieht das.',
    en: 'Why is this rejected? The requester will see it.',
  },
  rejectIsFinal: {
    de: 'Eine Ablehnung beendet den gesamten Antrag. Er kann danach nur noch kopiert werden.',
    en: 'A rejection ends the whole request. After that it can only be copied to a new draft.',
  },
  decidedOk: { de: 'Entscheidung erfasst.', en: 'Decision recorded.' },
  changeRecordRow: {
    de: 'Änderungsantrag — noch nicht mit dieser Ansicht verbunden.',
    en: 'Change record — not wired to this screen yet.',
  },

  // ─── purchase orders ──────────────────────────────────────────────────────
  ordersTitle: { de: 'Bestellungen', en: 'Purchase Orders' },
  ordersDesc: {
    de: 'Was tatsächlich bestellt wurde und zu welchem Preis.',
    en: 'What was actually ordered, and at what price.',
  },
  navOrders: { de: 'Bestellungen', en: 'Purchase Orders' },
  noOrders: { de: 'Noch keine Bestellungen.', en: 'No purchase orders yet.' },
  noOrdersMatch: { de: 'Keine Bestellung passt zum Filter.', en: 'No purchase order matches the filter.' },
  colPurchaseOrder: { de: 'Bestellung', en: 'Purchase order' },
  orderDialogTitle: { de: 'Bestellung {number}', en: 'Purchase order {number}' },
  issuedByOn: { de: 'Ausgelöst von {by} · {at}', en: 'Issued by {by} · {at}' },
  progressReceived: { de: '{received} von {ordered} erhalten', en: '{received} of {ordered} received' },
  openRequestAria: { de: 'Antrag {number} öffnen', en: 'Open request {number}' },
  keepOrder: { de: 'Behalten', en: 'Keep order' },
  cancelOrderBody: {
    de: 'Eine stornierte Bestellung ist beendet — gegen sie kann nichts mehr eingebucht werden. Bitte den Grund angeben.',
    en: 'A cancelled order is finished — nothing more can be received against it. Say why it is being cancelled.',
  },
  colSupplier: { de: 'Lieferant', en: 'Supplier' },
  colRequest: { de: 'Antrag', en: 'Request' },
  colTotal: { de: 'Summe', en: 'Total' },
  colIssued: { de: 'Ausgelöst', en: 'Issued' },
  colExpected: { de: 'Erwartet', en: 'Expected' },
  colProgress: { de: 'Fortschritt', en: 'Progress' },
  fieldSupplier: { de: 'Lieferant', en: 'Supplier' },
  supplierPlaceholder: { de: 'Lieferant eingeben oder wählen…', en: 'Type or pick a supplier…' },
  supplierRequired: { de: 'Der Lieferant muss benannt werden.', en: 'Name the supplier.' },
  supplierUnknown: {
    de: 'Nicht im Lieferantenstamm — wird als Freitext gespeichert.',
    en: 'Not in the supplier master — stored as free text.',
  },
  fieldSupplierRef: { de: 'Referenz des Lieferanten', en: 'Supplier reference' },
  fieldExpectedDate: { de: 'Erwartetes Lieferdatum', en: 'Expected date' },
  fieldShipTo: { de: 'Lieferadresse', en: 'Ship to' },
  fieldUnitPrice: { de: 'Preis/Stk.', en: 'Unit price' },
  fieldWarrantyMonths: { de: 'Garantie (Monate)', en: 'Warranty (months)' },
  orderQty: { de: 'Menge bestellen', en: 'Order qty' },
  outstandingQty: { de: 'Noch offen: {qty}', en: 'Still unordered: {qty}' },
  zeroLeavesOut: {
    de: '0 lässt die Position aus dieser Bestellung heraus.',
    en: '0 leaves the line out of this order.',
  },
  overOrderRefused: {
    de: 'Mehr als beantragt kann nicht bestellt werden.',
    en: 'Ordering more than was requested is refused.',
  },
  partialOrderNote: {
    de: 'Eine Teilbestellung ist normal — der Rest kann später bestellt werden.',
    en: 'A partial order is normal — the remainder can be ordered later.',
  },
  issueOrderTitle: { de: 'Bestellung auslösen', en: 'Issue a purchase order' },
  issuedOk: { de: 'Bestellung ausgelöst.', en: 'Purchase order issued.' },
  cancelOrder: { de: 'Bestellung stornieren', en: 'Cancel order' },
  cancelOrderTitle: { de: 'Bestellung stornieren?', en: 'Cancel this purchase order?' },
  orderCancelledOk: { de: 'Bestellung storniert.', en: 'Purchase order cancelled.' },
  orderLines: { de: 'Bestellpositionen', en: 'Order lines' },
  colOutstanding: { de: 'Offen', en: 'Outstanding' },
  noLinesSelected: {
    de: 'Mindestens eine Position mit Menge > 0 wird benötigt.',
    en: 'At least one line needs a quantity above zero.',
  },

  // ─── approval rules ───────────────────────────────────────────────────────
  rulesTitle: { de: 'Genehmigungsregeln', en: 'Approval Rules' },
  rulesDesc: {
    de: 'Wer wann unterschreibt. Muss vor dem ersten Antrag existieren.',
    en: 'Who signs off and when. Has to exist before the first request.',
  },
  navRules: { de: 'Genehmigungsregeln', en: 'Approval Rules' },
  noRules: { de: 'Noch keine Regel.', en: 'No rules yet.' },
  noRulesWarning: {
    de: 'Solange keine Regel existiert, wird jeder Antrag ohne Prüfung genehmigt.',
    en: 'While no rule exists, every request is approved with nobody reviewing it.',
  },
  newRule: { de: 'Neue Regel', en: 'New rule' },
  editRule: { de: 'Regel bearbeiten', en: 'Edit rule' },
  colRuleName: { de: 'Regel', en: 'Rule' },
  colApprover: { de: 'Genehmiger', en: 'Approver' },
  colMode: { de: 'Modus', en: 'Mode' },
  colScope: { de: 'Geltungsbereich', en: 'Scope' },
  colActive: { de: 'Aktiv', en: 'Active' },
  // ⚠ R-309 — ONE engine, TWO queues. `record_type` is the only thing separating a
  // procurement rule from a change rule, and the console used to hardcode
  // `procurement` — so every rule an operator created was a purchasing rule and a
  // CHANGE never matched one.
  // ⚠ R-309 — a rejection means two different things. A REQUEST ends; a CHANGE goes
  // back to an editable Plan. One warning for both would be wrong in one direction.
  decideReasonLabel: { de: 'Grund für die Ablehnung', en: 'Reason for rejecting' },

  rejectSendsChangeBack: {
    de: 'Eine Ablehnung beendet diesen Change nicht — er geht bearbeitbar in die Planungsphase zurück, damit die antragstellende Person ihn korrigieren kann. Der Grund erscheint auf dem Datensatz.',
    en: 'Rejecting does not end this change — it goes back to an editable Plan so the requester can fix it. Your reason is recorded on the record.',
  },

  fieldAppliesTo: { de: 'Gilt für', en: 'Applies to' },
  colAppliesTo: { de: 'Gilt für', en: 'Applies to' },
  appliesProcurement: { de: 'Beschaffungsanträge', en: 'Procurement requests' },
  appliesChange: { de: 'Change-Anträge', en: 'Change requests' },
  appliesToHint: {
    de: 'Eine Engine bedient beide Warteschlangen. Eine Regel greift nur beim hier genannten Datensatztyp — ein Change wird also nie von einer Beschaffungsregel aufgehalten und umgekehrt.',
    en: 'One engine serves both queues. A rule only ever matches the record type named here — so a change is never held up by a purchasing rule, and vice versa.',
  },
  valueBoundsChangeNote: {
    de: 'Wertgrenzen gelten nur für die Beschaffung. Ein Change trägt keinen Betrag, daher kann eine Change-Regel mit Wertgrenze niemals greifen — die Felder werden für Change-Regeln ausgelassen.',
    en: 'Value bounds are procurement-only. A change carries no money, so a change rule with a value bound could never match — the fields are omitted for change rules rather than left as a trap.',
  },
  simulateChangeNote: {
    de: 'Ein Change trägt keinen Wert, daher sind wertgebundene Regeln hier ausgeschlossen.',
    en: 'A change carries no value, so value-bounded rules are excluded when this is set to Change.',
  },

  fieldRuleName: { de: 'Name', en: 'Name' },
  phLineQty: { de: 'z. B. 4', en: 'e.g. 4' },
  phUnitCost: { de: 'z. B. 1.250,00', en: 'e.g. 1,250.00' },
  phCancelRequestReason: {
    de: 'Warum wird diese Anfrage storniert?',
    en: 'Why is this request being cancelled?',
  },
  phSupplierRef: {
    de: 'z. B. Angebots- oder Auftragsnummer des Lieferanten',
    en: "e.g. the supplier's quote or order number",
  },
  phShipTo: {
    de: 'z. B. LDS-WHS-SPARES oder eine Lieferadresse',
    en: 'e.g. LDS-WHS-SPARES or a delivery address',
  },
  phOrderQty: { de: 'z. B. 2', en: 'e.g. 2' },
  phWarranty: { de: 'z. B. 12', en: 'e.g. 12' },
  phRuleName: { de: 'z. B. Investitionen über AED 20.000', en: 'e.g. Capex above AED 20,000' },
  phLevel: { de: 'z. B. 1', en: 'e.g. 1' },
  phMinValue: { de: 'z. B. 0,00', en: 'e.g. 0.00' },
  phMaxValue: { de: 'Leer = keine Obergrenze', en: 'Blank = no upper bound' },
  phSimValue: { de: 'z. B. 25.000,00', en: 'e.g. 25,000.00' },
  phApprover: { de: 'Genehmigende Person wählen', en: 'Select an approver' },
  phMode: { de: 'Modus wählen', en: 'Select a mode' },
  fieldLevel: { de: 'Stufe', en: 'Level' },
  fieldMode: { de: 'Modus', en: 'Mode' },
  fieldApprover: { de: 'Genehmiger', en: 'Approver' },
  fieldClassCode: { de: 'Anlagenklasse', en: 'Asset class' },
  fieldMinValue: { de: 'Ab Wert', en: 'From value' },
  fieldMaxValue: { de: 'Bis Wert', en: 'Up to value' },
  fieldActive: { de: 'Aktiv', en: 'Active' },
  activeYes: { de: 'Ja', en: 'Yes' },
  activeNo: { de: 'Nein', en: 'No' },
  anyClass: { de: 'Jede Klasse', en: 'Any class' },
  noBound: { de: 'Keine Grenze', en: 'No bound' },
  modeExplainer: {
    de: '«Alle» verlangt jeden Genehmiger dieser Stufe, «Eine/r» genügt mit einer Unterschrift.',
    en: '"All" needs every approver on the level; "Any" is satisfied by one signature.',
  },
  approverGone: {
    de: 'Dieser Genehmiger ist nicht mehr im Verzeichnis — die Regel kann nicht greifen.',
    en: 'This approver has left the directory — the rule cannot fire.',
  },
  ruleSavedOk: { de: 'Regel gespeichert.', en: 'Rule saved.' },
  ruleDeletedOk: { de: 'Regel gelöscht.', en: 'Rule deleted.' },
  deleteRuleTitle: { de: 'Regel löschen?', en: 'Delete this rule?' },
  deleteRuleKeeps: {
    de: 'Bereits erzeugte Genehmigungszeilen bleiben bestehen — eine laufende Kette wird nicht umgebaut.',
    en: 'Approval rows already built from it survive — an in-flight chain is never re-shaped.',
  },
  approvalsKept: {
    de: '{count} bestehende Genehmigungszeilen bleiben unverändert.',
    en: '{count} existing approval rows were left untouched.',
  },

  simulateTitle: { de: 'Kette simulieren', en: 'Simulate the chain' },
  simulateDesc: {
    de: 'Wer würde einen solchen Antrag unterschreiben? Es wird nichts erzeugt.',
    en: 'Who would sign a request like this? Nothing is created.',
  },
  simulateValue: { de: 'Wert', en: 'Value' },
  simulateRun: { de: 'Simulieren', en: 'Simulate' },
  simulateAutoApprove: {
    de: 'Ein solcher Antrag würde ohne Prüfung genehmigt.',
    en: 'A request like this would be approved with nobody in the loop.',
  },
} satisfies Namespace;
